import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Microphone, Stop, ArrowClockwise } from "@phosphor-icons/react";
import { api, formatApiErrorDetail } from "../../lib/api";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const MAX_SECONDS = 120;

export const SpeakingStep = ({ task, sessionId, result, onResult, kind = "picture" }) => {
  const [recording, setRecording] = useState(false);
  const [seconds, setSeconds] = useState(0);
  const [busy, setBusy] = useState(false);
  const rec = useRef(null);
  const timer = useRef(null);
  const takesLeft = result?.takesLeft ?? task.max_takes;

  useEffect(() => () => { clearInterval(timer.current); rec.current?.stream?.getTracks().forEach((t) => t.stop()); }, []);

  const upload = async (blob) => {
    setBusy(true);
    const fd = new FormData();
    fd.append("session_id", sessionId);
    fd.append("task", kind);
    fd.append("audio", blob, blob.type.includes("mp4") ? "speaking.mp4" : "speaking.webm");
    try {
      const { data } = await api.post("/test/speaking/transcribe", fd, { timeout: 90000 });
      onResult({ transcript: data.transcript, takesLeft: data.takes_left });
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not process the recording.");
    } finally {
      setBusy(false);
    }
  };

  const start = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream);
      const chunks = [];
      mr.ondataavailable = (e) => e.data.size && chunks.push(e.data);
      mr.onstop = () => {
        stream.getTracks().forEach((t) => t.stop());
        upload(new Blob(chunks, { type: mr.mimeType || "audio/webm" }));
      };
      rec.current = mr;
      mr.start();
      setSeconds(0);
      setRecording(true);
      timer.current = setInterval(() => setSeconds((s) => {
        if (s + 1 >= MAX_SECONDS) stop();
        return s + 1;
      }), 1000);
    } catch {
      toast.error("Microphone not available. Please allow microphone access and try again.");
    }
  };

  const stop = () => {
    clearInterval(timer.current);
    setRecording(false);
    if (rec.current?.state === "recording") rec.current.stop();
  };

  const words = (result?.transcript || "").split(/\s+/).filter(Boolean).length;
  const isPicture = kind === "picture";

  return (
    <div data-testid={isPicture ? "test-speaking-step" : "test-speaking-question-step"} className="pb-16 sm:pb-0">
      <div className="text-xs font-mono-fig text-muted-foreground">{isPicture ? "Speaking · Describe the picture" : "Speaking · Answer the question"}</div>
      <h2 className="text-2xl md:text-3xl mt-3 font-bold leading-snug max-w-3xl" data-testid="test-question-prompt">{task.prompt}</h2>
      {!isPicture && <p className="mt-2 text-sm text-foreground/75 max-w-3xl">Answer out loud for about 1 minute. Give details, examples and reasons.</p>}
      <div className={`mt-6 ${isPicture ? "grid lg:grid-cols-2 gap-8 items-start" : "max-w-3xl"}`}>
        {isPicture && <img src={`${BACKEND_URL}${task.image_url}`} alt="Describe this" className="w-full max-h-[40vh] lg:max-h-none object-contain border border-foreground" data-testid="test-speaking-image" />}
        <div>
          <div className="flex items-center gap-4">
            <button
              onClick={recording ? stop : start}
              disabled={busy || (!recording && takesLeft <= 0)}
              data-testid="test-speaking-record-btn"
              className={`w-16 h-16 flex items-center justify-center shrink-0 disabled:opacity-40 ${recording ? "bg-red-600 text-white border border-red-600" : "neu-btn-primary"}`}
            >
              {recording ? <Stop size={24} weight="fill" /> : result?.transcript ? <ArrowClockwise size={24} weight="bold" /> : <Microphone size={24} weight="fill" />}
            </button>
            <div>
              <div className="font-bold" data-testid="test-speaking-status">
                {busy ? "Processing your recording…" : recording ? `Recording… ${seconds}s / ${MAX_SECONDS}s` : result?.transcript ? "Recorded — tap to record again" : "Tap to start recording"}
              </div>
              <div className="text-xs text-muted-foreground font-mono-fig" data-testid="test-speaking-takes-left">
                {takesLeft} of {task.max_takes} recordings left · aim for about 1 minute
              </div>
            </div>
          </div>
          {result?.transcript !== undefined && (
            <div className="mt-6 neu-inset p-5" data-testid="test-speaking-transcript">
              <div className="overline">What we heard · {words} words</div>
              <p className="mt-2 text-sm leading-relaxed">{result.transcript || "(nothing was heard — please try again)"}</p>
              {words < task.min_words && <p className="mt-2 text-xs text-muted-foreground">Try to say at least {task.min_words} words.</p>}
            </div>
          )}
          <p className="mt-6 text-xs text-muted-foreground">Your most recent recording is the one that counts.</p>
        </div>
      </div>
    </div>
  );
};
